import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { tokenManager } from '../lib/api-client';

export type ServiceType = 'taxi' | 'bus' | 'cargo';

export interface ServiceInfo {
  key: ServiceType;
  label: string;
  description: string;
  isAvailable: boolean;
}

export const services: ServiceInfo[] = [
  {
    key: 'taxi',
    label: 'Shaharlararo Taxi',
    description: "Shaharlararo taxi va yuk tashish (40 kg gacha)",
    isAvailable: true,
  },
  {
    key: 'bus',
    label: 'Avtobuslar',
    description: 'Avtobus kompaniyalari va reyslarni boshqarish',
    isAvailable: true,
  },
  {
    key: 'cargo',
    label: 'Cargo yuk tashish',
    description: "Katta yuklar (40 kg dan yuqori)",
    isAvailable: false,
  },
];

const SERVICE_STORAGE_KEY = 'safaro_admin_service';

// Service mode controlled by environment variable
// VITE_SERVICE_MODE=bus → bus-only admin (bus-admin.safaro.uz)
// VITE_SERVICE_MODE=taxi → taxi-only admin (taxi.safaro.uz)
// unset → multi-service (shows service switcher)
const SERVICE_MODE = import.meta.env.VITE_SERVICE_MODE as string | undefined;
const BUS_ONLY_MODE = SERVICE_MODE === 'bus';
const TAXI_ONLY_MODE = SERVICE_MODE === 'taxi';

// Detect service from URL path
const detectServiceFromPath = (pathname: string): ServiceType | null => {
  // In locked mode, always return the locked service
  if (BUS_ONLY_MODE) return 'bus';
  if (TAXI_ONLY_MODE) return 'taxi';

  // Bus-specific paths (all start with /bus/)
  if (pathname.startsWith('/bus/') || pathname === '/bus') {
    return 'bus';
  }
  // Taxi-specific paths (including shared pages without /bus/ prefix)
  if (
    pathname.startsWith('/drivers') ||
    pathname.startsWith('/passengers') ||
    pathname.startsWith('/trips') ||
    pathname.startsWith('/documents') ||
    pathname.startsWith('/live-map') ||
    pathname.startsWith('/pricing') ||
    pathname.startsWith('/incentives') ||
    pathname.startsWith('/referrals') ||
    pathname.startsWith('/users') ||
    pathname.startsWith('/support') ||
    pathname.startsWith('/faq') ||
    pathname.startsWith('/promo-codes') ||
    pathname.startsWith('/staff') ||
    pathname.startsWith('/notifications') ||
    pathname.startsWith('/analytics') ||
    pathname.startsWith('/payments')
  ) {
    return 'taxi';
  }
  return null;
};

// Get initial service based on mode
const getInitialService = (): ServiceType => {
  if (BUS_ONLY_MODE) return 'bus';
  if (TAXI_ONLY_MODE) return 'taxi';

  // Bus company users are locked to bus service
  if (isBusCompanyUser()) return 'bus';

  // First check URL
  const pathService = detectServiceFromPath(window.location.pathname);
  if (pathService) {
    return pathService;
  }

  // Then check localStorage
  const stored = localStorage.getItem(SERVICE_STORAGE_KEY);
  if (stored && (stored === 'taxi' || stored === 'bus' || stored === 'cargo')) {
    return stored as ServiceType;
  }

  return 'taxi';
};

// Check if current user is a bus company
export const isBusCompanyUser = () => {
  const user = tokenManager.getUser();
  return user?.userType === 'bus_company';
};

// Export mode flags for use in other components
export const isBusOnlyMode = () => BUS_ONLY_MODE;
export const isTaxiOnlyMode = () => TAXI_ONLY_MODE;
export const isLockedMode = () => BUS_ONLY_MODE || TAXI_ONLY_MODE;

interface ServiceContextType {
  selectedService: ServiceType;
  setSelectedService: (service: ServiceType) => void;
  currentServiceInfo: ServiceInfo;
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

export const ServiceProvider = ({ children }: { children: ReactNode }) => {
  const [selectedService, setSelectedServiceState] = useState<ServiceType>(getInitialService);

  // Persist to localStorage when service changes (no-op in locked mode or bus company)
  const setSelectedService = (service: ServiceType) => {
    if (BUS_ONLY_MODE || TAXI_ONLY_MODE || isBusCompanyUser()) {
      return; // Do nothing in locked mode or for bus company users
    }
    localStorage.setItem(SERVICE_STORAGE_KEY, service);
    setSelectedServiceState(service);
  };

  // On mount, sync with URL if needed
  useEffect(() => {
    const pathService = detectServiceFromPath(window.location.pathname);
    if (pathService && pathService !== selectedService) {
      setSelectedService(pathService);
    }
  }, []);

  const currentServiceInfo = services.find(s => s.key === selectedService) || services[0];

  return (
    <ServiceContext.Provider
      value={{
        selectedService,
        setSelectedService,
        currentServiceInfo,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export const useService = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useService must be used within a ServiceProvider');
  }
  return context;
};
